/* Builder B · Équipe as one list (Scope v5 §8, batch 22 · Roadmap 0.4).
   `api.ts` in this folder is Builder A's Défi seam — this file is B's, so the
   two never edit each other.

   The people are @/lib/team's members, read as they are: never a second list.
   What a member *did* is derived, never stored here:
     · scans · participations — from her personal support (`supportNumbers`),
       the attribution rule « participation → support → membre »;
     · cadeaux validés — the till's PIN ledger, the only number the members
       seam cannot derive: GET /v1/app/members/ledger (contract 09 §9.1, last
       30 days, net of undos), keyed by member id.
   Her access words come from Compte et accès (`ROLE_LABEL`, `ROLE_LINES`), never restated.
   The members row: with an API the server counts all three over 30 days (09 §9.1, each member's `numbers`), so neither
   the ledger nor the supports' figures are read; a staff login gets its own numbers only. */
import { useQuery } from '@tanstack/react-query'
import { ApiError, apiWrites, get } from '@/lib/api'
import type { Member } from '@/lib/team'
import { supportNumbers, type SupportRef } from '@/lib/campaign'
import { refusalLines } from '@/lib/campaign-api'
import { ROLE_LABEL, ROLE_LINES } from '@/routes/parametres/api'

export interface MemberLedger {
  /** gifts validated at the till with this member's PIN, by member id */
  validated: Record<string, number>
}

/** The demo's ledger; with an API nothing is read (the members' own numbers carry it). */
export const useMemberLedger = () =>
  useQuery({ queryKey: ['equipe-membres'], queryFn: () => get<MemberLedger>('/app/members/ledger'), enabled: !apiWrites })

/** A member's own QR: the support of genre « personnel » that carries her id. */
export const personalOf = (m: Member, all: SupportRef[]) =>
  all.find(s => s.kind === 'personnel' && s.memberId === m.id)

/** « Accès » in the merchant's words — none, or the login role from Compte et accès. */
export const accessLabel = (m: Member) => (m.access === 'none' ? 'aucun' : ROLE_LABEL[m.access])
/** What a login's role does (V16's line), printed under « Accès »; none for a member without a login. */
export const accessLine = (m: Member): string | null => (m.access === 'none' ? null : ROLE_LINES[m.access])

export interface MemberRow {
  member: Member
  support: SupportRef | undefined
  /** null when she has no personal support: nothing can be attributed to her by scan */
  scans: number | null
  participations: number | null
  /** null with an API on another member's row for a staff login (her own numbers only) */
  validated: number | null
}
export function memberRow(m: Member, all: SupportRef[], ledger: MemberLedger | undefined): MemberRow {
  const support = personalOf(m, all)
  if (apiWrites) {
    const k = m.numbers ?? null
    return { member: m, support, scans: support && k ? k.scans30d : null, participations: support && k ? k.participations30d : null, validated: k ? k.giftsValidated30d : null }
  }
  const n = support ? supportNumbers(support) : null
  return {
    member: m, support,
    scans: n ? n.scans30 : null,
    participations: n ? n.participations : null,
    validated: ledger?.validated[m.id] ?? 0,
  }
}

/** Why a member write was refused, in the app's words (C-2: the API's own `message` is never shown). */
export function memberRefusal(e: unknown): string {
  if (!(e instanceof ApiError) || e.status === 401) return refusalLines(e)[0]
  switch (e.code) {
    case 'forbidden_role': return 'Seuls le propriétaire, les administrateurs et les managers gèrent l’équipe.'
    case 'not_found': return e.field === 'locationId'
      ? 'Cet établissement n’existe pas, ou plus, sur le serveur. Rechargez la page.'
      : 'Ce membre n’existe pas, ou plus, sur le serveur. Rechargez la page.'
  }
  return 'L’action n’a pas abouti : rien n’a changé. Réessayez dans un instant.'
}
